import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FiSave, FiArrowLeft, FiUpload, FiX } from 'react-icons/fi';
import expenseService from '../services/expenseService';
import technicianService from '../services/technicianService';
import helperService from '../services/helperService';
import toolService from '../services/toolService';
import siteVisitService from '../services/siteVisitService';
import Input from '../components/Input';
import Button from '../components/Button';
import './TechnicianForm.css';

const ExpenseForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [technicians, setTechnicians] = useState([]);
  const [helpers, setHelpers] = useState([]);
  const [tools, setTools] = useState([]);
  const [siteVisits, setSiteVisits] = useState([]);
  const [receiptPreview, setReceiptPreview] = useState(null);
  const [receiptFile, setReceiptFile] = useState(null);

  const [formData, setFormData] = useState({
    category: 'other',
    amount: '',
    date: new Date().toISOString().split('T')[0],
    description: '',
    paidTo: '',
    paymentMethod: '',
    relatedTo: '',
    relatedToModel: '',
    notes: '',
    recurring: {
      isRecurring: false,
      frequency: '',
      nextDueDate: '',
    },
  });

  const categories = [
    'fuel',
    'repairs',
    'salaries',
    'rent',
    'utilities',
    'tools',
    'inventory',
    'marketing',
    'insurance',
    'other',
  ];

  const paymentMethods = ['cash', 'upi', 'card', 'bank_transfer', 'cheque'];
  const relatedToModels = ['Technician', 'Helper', 'Tool', 'SiteVisit'];
  const frequencies = ['daily', 'weekly', 'monthly', 'yearly'];

  useEffect(() => {
    fetchRelatedData();
    if (isEdit) {
      fetchExpense();
    }
  }, [id]);

  const fetchRelatedData = async () => {
    try {
      const [techResponse, helperResponse, toolResponse, visitResponse] = await Promise.all([
        technicianService.getAll(),
        helperService.getAll(),
        toolService.getAll(),
        siteVisitService.getAll(),
      ]);

      setTechnicians(techResponse.data || []);
      setHelpers(helperResponse.data || []);
      setTools(toolResponse.data || []);
      setSiteVisits(visitResponse.data || []);
    } catch (error) {
      console.error('Failed to fetch related data:', error);
    }
  };

  const fetchExpense = async () => {
    try {
      const response = await expenseService.getById(id);
      const expense = response.data;
      
      setFormData({
        category: expense.category,
        amount: expense.amount,
        date: expense.date ? new Date(expense.date).toISOString().split('T')[0] : '',
        description: expense.description || '',
        paidTo: expense.paidTo || '',
        paymentMethod: expense.paymentMethod || '',
        relatedTo: expense.relatedTo?._id || '',
        relatedToModel: expense.relatedToModel || '',
        notes: expense.notes || '',
        recurring: {
          isRecurring: expense.recurring?.isRecurring || false,
          frequency: expense.recurring?.frequency || '',
          nextDueDate: expense.recurring?.nextDueDate
            ? new Date(expense.recurring.nextDueDate).toISOString().split('T')[0]
            : '',
        },
      });

      if (expense.receipt?.filename) {
        setReceiptPreview(
          `${import.meta.env.VITE_API_BASE_URL || 'http://localhost:5000'}/uploads/receipts/${expense.receipt.filename}`
        );
      }
    } catch (error) {
      toast.error('Failed to fetch expense details');
      navigate('/expenses');
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name.startsWith('recurring.')) {
      const field = name.split('.')[1];
      setFormData({
        ...formData,
        recurring: {
          ...formData.recurring,
          [field]: type === 'checkbox' ? checked : value,
        },
      });
    } else {
      setFormData({
        ...formData,
        [name]: value,
      });
    }
  };

  const handleReceiptChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('File size must be less than 5MB');
        return;
      }
      setReceiptFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setReceiptPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeReceipt = () => {
    setReceiptFile(null);
    setReceiptPreview(null);
  };

  const getRelatedOptions = () => {
    switch (formData.relatedToModel) {
      case 'Technician':
        return technicians.map((tech) => ({
          value: tech._id,
          label: `${tech.name} (${tech.employeeId})`,
        }));
      case 'Helper':
        return helpers.map((helper) => ({
          value: helper._id,
          label: `${helper.name} (${helper.employeeId})`,
        }));
      case 'Tool':
        return tools.map((tool) => ({
          value: tool._id,
          label: `${tool.toolName} (${tool.toolCode})`,
        }));
      case 'SiteVisit':
        return siteVisits.map((visit) => ({
          value: visit._id,
          label: `${visit.ticketNumber} - ${visit.customer.name}`,
        }));
      default:
        return [];
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const submitData = {
      category: formData.category,
      amount: Number(formData.amount),
      date: formData.date,
      description: formData.description,
      paidTo: formData.paidTo || undefined,
      paymentMethod: formData.paymentMethod || undefined,
      relatedTo: formData.relatedTo || undefined,
      relatedToModel: formData.relatedToModel || undefined,
      notes: formData.notes || undefined,
      recurring: formData.recurring.isRecurring
        ? {
            isRecurring: true,
            frequency: formData.recurring.frequency || undefined,
            nextDueDate: formData.recurring.nextDueDate || undefined,
          }
        : undefined,
    };

    if (receiptFile) {
      submitData.receipt = receiptFile;
    }

    try {
      if (isEdit) {
        await expenseService.update(id, submitData);
        toast.success('Expense updated successfully');
      } else {
        await expenseService.create(submitData);
        toast.success('Expense added successfully');
      }
      navigate('/expenses');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Operation failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="form-container">
      <div className="form-header">
        <button onClick={() => navigate('/expenses')} className="back-button">
          <FiArrowLeft /> Back
        </button>
        <h2 className="form-title">
          {isEdit ? 'Edit Expense' : 'Add New Expense'}
        </h2>
      </div>

      <form onSubmit={handleSubmit} className="form-card">
        <div className="form-section">
          <h3 className="section-title">Expense Information</h3>
          <div className="form-grid">
            <div className="input-group">
              <label className="input-label">
                Category <span className="required-asterisk">*</span>
              </label>
              <select
                name="category"
                value={formData.category}
                onChange={handleChange}
                className="input-field"
                required
              >
                {categories.map((cat) => (
                  <option key={cat} value={cat}>
                    {cat.charAt(0).toUpperCase() + cat.slice(1)}
                  </option>
                ))}
              </select>
            </div>

            <Input
              label="Amount (₹)"
              type="number"
              name="amount"
              value={formData.amount}
              onChange={handleChange}
              placeholder="0.00"
              min="0"
              step="0.01"
              required
            />

            <Input
              label="Date"
              type="date"
              name="date"
              value={formData.date}
              onChange={handleChange}
              required
            />

            <Input
              label="Description"
              type="text"
              name="description"
              value={formData.description}
              onChange={handleChange}
              placeholder="Brief description of the expense"
              required
            />
          </div>
        </div>

        <div className="form-section">
          <h3 className="section-title">Payment Details</h3>
          <div className="form-grid">
            <Input
              label="Paid To"
              type="text"
              name="paidTo"
              value={formData.paidTo}
              onChange={handleChange}
              placeholder="Vendor/Person name"
            />

            <div className="input-group">
              <label className="input-label">Payment Method</label>
              <select
                name="paymentMethod"
                value={formData.paymentMethod}
                onChange={handleChange}
                className="input-field"
              >
                <option value="">Select Payment Method</option>
                {paymentMethods.map((method) => (
                  <option key={method} value={method}>
                    {method.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div className="form-section">
          <h3 className="section-title">Receipt</h3>
          <div className="form-grid">
            <div className="input-group">
              <label className="input-label">Upload Receipt (Optional)</label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                <input
                  type="file"
                  accept="image/*,.pdf"
                  onChange={handleReceiptChange}
                  className="input-field"
                  style={{ padding: '0.5rem' }}
                />
                {receiptPreview && (
                  <div style={{ position: 'relative', display: 'inline-block' }}>
                    {receiptPreview.startsWith('data:') || receiptPreview.endsWith('.pdf') ? (
                      <div style={{ padding: '1rem', border: '1px solid #e5e7eb', borderRadius: '8px' }}>
                        {receiptPreview.endsWith('.pdf') ? (
                          <a
                            href={receiptPreview}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: '#3b82f6', textDecoration: 'underline' }}
                          >
                            View PDF Receipt
                          </a>
                        ) : (
                          <img
                            src={receiptPreview}
                            alt="Receipt preview"
                            style={{ maxWidth: '200px', maxHeight: '200px', borderRadius: '4px' }}
                          />
                        )}
                      </div>
                    ) : (
                      <a
                        href={receiptPreview}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: '#3b82f6', textDecoration: 'underline' }}
                      >
                        View Current Receipt
                      </a>
                    )}
                    <button
                      type="button"
                      onClick={removeReceipt}
                      style={{
                        position: 'absolute',
                        top: '0.5rem',
                        right: '0.5rem',
                        background: '#ef4444',
                        color: 'white',
                        border: 'none',
                        borderRadius: '50%',
                        width: '24px',
                        height: '24px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <FiX size={14} />
                    </button>
                  </div>
                )}
                <p style={{ fontSize: '0.875rem', color: '#6b7280', margin: 0 }}>
                  Supported formats: JPG, PNG, PDF (Max 5MB)
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="form-section">
          <h3 className="section-title">Related To (Optional)</h3>
          <div className="form-grid">
            <div className="input-group">
              <label className="input-label">Related To Type</label>
              <select
                name="relatedToModel"
                value={formData.relatedToModel}
                onChange={handleChange}
                className="input-field"
              >
                <option value="">None</option>
                {relatedToModels.map((model) => (
                  <option key={model} value={model}>
                    {model}
                  </option>
                ))}
              </select>
            </div>

            {formData.relatedToModel && (
              <div className="input-group">
                <label className="input-label">Select {formData.relatedToModel}</label>
                <select
                  name="relatedTo"
                  value={formData.relatedTo}
                  onChange={handleChange}
                  className="input-field"
                >
                  <option value="">Select {formData.relatedToModel}</option>
                  {getRelatedOptions().map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
        </div>

        <div className="form-section">
          <h3 className="section-title">Recurring Expense (Optional)</h3>
          <div className="form-grid">
            <div className="input-group">
              <label className="input-label" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <input
                  type="checkbox"
                  name="recurring.isRecurring"
                  checked={formData.recurring.isRecurring}
                  onChange={handleChange}
                />
                <span>This is a recurring expense</span>
              </label>
            </div>

            {formData.recurring.isRecurring && (
              <>
                <div className="input-group">
                  <label className="input-label">Frequency</label>
                  <select
                    name="recurring.frequency"
                    value={formData.recurring.frequency}
                    onChange={handleChange}
                    className="input-field"
                  >
                    <option value="">Select Frequency</option>
                    {frequencies.map((freq) => (
                      <option key={freq} value={freq}>
                        {freq.charAt(0).toUpperCase() + freq.slice(1)}
                      </option>
                    ))}
                  </select>
                </div>

                <Input
                  label="Next Due Date"
                  type="date"
                  name="recurring.nextDueDate"
                  value={formData.recurring.nextDueDate}
                  onChange={handleChange}
                />
              </>
            )}
          </div>
        </div>

        <div className="form-section">
          <h3 className="section-title">Additional Notes</h3>
          <div className="form-grid">
            <div className="input-group">
              <label className="input-label">Notes</label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                className="input-field"
                rows="4"
                placeholder="Any additional notes about this expense..."
              />
            </div>
          </div>
        </div>

        <div className="form-actions">
          <Button
            type="button"
            variant="secondary"
            onClick={() => navigate('/expenses')}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            icon={<FiSave />}
            loading={loading}
          >
            {isEdit ? 'Update Expense' : 'Add Expense'}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default ExpenseForm;
